import { useEffect, useMemo, useState } from "react";
import {
  fetchFindModelCategories,
  fetchFindModelNumber,
  findModelImageUrl,
  type FindModelCategory,
  type FindModelGuide,
  type FindModelProduct,
} from "../api/findMyModel";
import { normalizeFindModelLocale } from "../lib/findModelLocale";
import { useLabel } from "../lib/labels";
import { Button } from "../ui";

interface FindMyModelPayload {
  title?: string;
  description?: string;
  locale?: string;
  metadata?: {
    locale?: string;
    [key: string]: unknown;
  };
  selects?: Array<{
    id?: string;
    options?: Array<{ label?: string; value?: string }>;
  }>;
  contents?: Record<string, { image?: string; alt?: string; description?: string }>;
}

interface Props {
  data: FindMyModelPayload;
  sessionToken: string;
}

/* Figma "Trailing Icon" union — 드롭다운 chevron. */
function Chevron() {
  return (
    <svg
      className="ac-find-model__chevron"
      width="12"
      height="6"
      viewBox="0 0 12 6"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 1L6 5L11 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function categoryId(category: FindModelCategory): string {
  return String(category.superCategoryId || category.id || "");
}

function categoryLabel(category: FindModelCategory): string {
  return String(category.superCategoryName || category.name || category.title || categoryId(category));
}

function productId(product: FindModelProduct): string {
  return String(product.categoryId || product.id || "");
}

function productLabel(product: FindModelProduct): string {
  return String(product.categoryName || product.name || product.title || productId(product));
}

function descLines(desc?: string): string[] {
  return (desc || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function fallbackCategories(data: FindMyModelPayload): FindModelCategory[] {
  const categorySelect = data.selects?.[0];
  const productSelect = data.selects?.[1];
  const products = (productSelect?.options || []).map((option) => ({
    categoryId: option.value || "",
    categoryName: option.label || option.value || "",
  }));
  return (categorySelect?.options || []).map((option) => ({
    superCategoryId: option.value || "",
    superCategoryName: option.label || option.value || "",
    categoryList: products,
  }));
}

export default function FindMyModelCard({ data, sessionToken }: Props) {
  const urlLocale = new URLSearchParams(window.location.search).get("locale");
  const locale = normalizeFindModelLocale(data.metadata?.locale, data.locale, urlLocale);

  // i18n 라벨(서버 미주입 시 영어 폴백). i18n SOT: docs/domain/i18n.
  const titleLabel = useLabel("findModelTitle", "How to Find My Model");
  const categoryFieldLabel = useLabel("findModelCategoryLabel", "Category");
  const categoryPlaceholder = useLabel("findModelCategoryPlaceholder", "Select Category");
  const productFieldLabel = useLabel("findModelProductLabel", "Product");
  const productPlaceholder = useLabel("findModelProductPlaceholder", "Select Product");
  const searchLabel = useLabel("findModelSearch", "Search");
  const loadingLabel = useLabel("findModelLoading", "Loading guide...");
  const errorCategoriesLabel = useLabel(
    "findModelErrorCategories",
    "Could not load model guide options. Please try again later.",
  );
  const errorGuideLabel = useLabel(
    "findModelErrorGuide",
    "Could not load the selected guide. Please choose another product.",
  );

  const [categories, setCategories] = useState<FindModelCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  // 디자인: Search 클릭으로만 가이드 조회. `${catId}::${prodId}` 형태.
  const [searchKey, setSearchKey] = useState("");
  const [guide, setGuide] = useState<FindModelGuide | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingGuide, setLoadingGuide] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingCategories(true);
    setError(null);
    fetchFindModelCategories(locale, sessionToken)
      .then((items) => {
        if (cancelled) return;
        setCategories(items);
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = fallbackCategories(data);
        setCategories(fallback);
        if (fallback.length === 0) {
          setError(errorCategoriesLabel);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [data, locale, sessionToken, errorCategoriesLabel]);

  const selectedCategory = useMemo(
    () => categories.find((category) => categoryId(category) === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );
  const products = selectedCategory?.categoryList || [];
  const selectedProduct = products.find((product) => productId(product) === selectedProductId) || null;

  const categoryDisabled = loadingCategories || categories.length === 0;
  const productDisabled = !selectedCategory || products.length === 0;
  const canSearch = !!selectedCategory && !!selectedProduct;

  // Search 클릭 시점(searchKey)에만 가이드 조회.
  useEffect(() => {
    if (!searchKey) {
      setGuide(null);
      return;
    }
    const [superId, catId] = searchKey.split("::");
    let cancelled = false;
    setLoadingGuide(true);
    setError(null);
    fetchFindModelNumber(locale, superId, catId, sessionToken)
      .then((item) => {
        if (!cancelled) setGuide(item);
      })
      .catch(() => {
        if (!cancelled) {
          const fallback = data.contents?.[catId];
          setGuide(
            fallback
              ? {
                  normalizedModelImagePathName: fallback.image,
                  modelImageAltName: fallback.alt,
                  modelNumberDesc: fallback.description,
                }
              : null,
          );
          if (!fallback) {
            setError(errorGuideLabel);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingGuide(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchKey, data.contents, locale, sessionToken, errorGuideLabel]);

  const handleSearch = () => {
    if (!canSearch) return;
    setSearchKey(`${selectedCategoryId}::${selectedProductId}`);
  };

  const imageUrl = findModelImageUrl(
    guide?.normalizedModelImagePathName || guide?.modelImagePathName,
    locale,
  );
  const lines = descLines(guide?.modelNumberDesc);

  return (
    <section className="ac-find-model">
      <p className="ac-find-model__title">{data.title || titleLabel}</p>

      <div className="ac-find-model__fields">
        <div className={`ac-find-model__dropdown${categoryDisabled ? " is-disabled" : ""}`}>
          <span className="ac-find-model__dropdown-title">
            {categoryFieldLabel}
            <em className="ac-find-model__req">*</em>
          </span>
          <select
            className="ac-find-model__select"
            value={selectedCategoryId}
            disabled={categoryDisabled}
            onChange={(event) => {
              setSelectedCategoryId(event.target.value);
              setSelectedProductId("");
              setSearchKey("");
              setGuide(null);
            }}
          >
            <option value="">{categoryPlaceholder}</option>
            {categories.map((category) => (
              <option key={categoryId(category)} value={categoryId(category)}>
                {categoryLabel(category)}
              </option>
            ))}
          </select>
          <Chevron />
        </div>

        <div className={`ac-find-model__dropdown${productDisabled ? " is-disabled" : ""}`}>
          <span className="ac-find-model__dropdown-title">
            {productFieldLabel}
            <em className="ac-find-model__req">*</em>
          </span>
          <select
            className="ac-find-model__select"
            value={selectedProductId}
            disabled={productDisabled}
            onChange={(event) => {
              setSelectedProductId(event.target.value);
              setSearchKey("");
              setGuide(null);
            }}
          >
            <option value="">{productPlaceholder}</option>
            {products.map((product) => (
              <option key={productId(product)} value={productId(product)}>
                {productLabel(product)}
              </option>
            ))}
          </select>
          <Chevron />
        </div>
      </div>

      <Button
        variant="primary"
        fullWidth
        disabled={!canSearch || loadingGuide}
        onClick={handleSearch}
      >
        {searchLabel}
      </Button>

      {loadingGuide ? <p className="ac-find-model__status">{loadingLabel}</p> : null}
      {error ? <p className="ac-find-model__status ac-find-model__status--error">{error}</p> : null}

      {guide ? (
        <div className="ac-find-model__guide">
          {lines.length > 0 ? (
            <div className="ac-find-model__copy">
              {lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : null}
          {imageUrl ? (
            <div className="image-wrap img-model-num">
              <img
                className="ac-find-model__image"
                src={imageUrl}
                alt={guide.modelImageAltName || "Find my model guide"}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
